/**
 * In-game menu. The session is paused while it is open; realism, display and voice apply
 * live, and Restart starts a fresh session with the same settings. The mark beside the title is
 * a standard right-hand holding pattern.
 */
import { TIME_OF_DAY_LABEL, TIMES_OF_DAY, WEATHER, WEATHERS, type RealismLevel, type TimeOfDay, type Weather } from '@atc/core';
import { isFullscreen, onFullscreenChange } from '../fullscreen';
import { VIEWS } from '../game';
import { mountMenuNavigation } from '../menu-navigation';
import type { AiSummary } from '../session-ai';
import type { Layout, SessionSettings } from '../settings';

export interface PauseOptions {
  settings: SessionSettings;
  allowVoicePlayback?: boolean;
  allowSpeechInput?: boolean;
  /** What AI the paused session is using and what is left. */
  ai?: AiSummary;
  onResume(): void;
  onChange(patch: Partial<SessionSettings>): void;
  onFullscreen(): void;
  onRestart(): void;
  /** End the session and show the debrief. */
  onEnd(): void;
  briefingLabel?: string;
  onBriefing(): void;
  onFeedback(): void;
  /** Download the session log (`session-log.ts`). */
  onSessionLog?(): void;
  onQuit(): void;
}

const LEVEL_LABEL: Record<RealismLevel, string> = { low: 'Low', mid: 'Mid', high: 'High' };
const GRAPHICS: [string, string][] = [['auto', 'Automatic'], ['3', 'High'], ['2', 'Balanced'], ['1', 'Low'], ['0', 'Minimal']];
const PROCEDURES: [SessionSettings['procedures'], string][] = [['active', 'Runways in use'], ['all', 'All'], ['off', 'Off']];
const RADIO_NOISE: [SessionSettings['radioNoise'], string][] = [['off', 'Clean'], ['low', 'Light'], ['normal', 'Full']];
const EMERGENCIES: [SessionSettings['emergencies'], string][] = [['rare', 'Rare (about one an hour)'], ['normal', 'Regular'], ['off', 'None']];
const esc = (t: string) => t.replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' })[c]!);
/** Inbound leg along the top to the fix, right turns. */
const HOLD = 'M20 10 H56 A10 10 0 0 1 56 30 H20 A10 10 0 0 1 20 10 Z';

export function showPause(root: HTMLElement, o: PauseOptions): () => void {
  const s = o.settings;
  const reducedMotion = typeof matchMedia === 'function' && matchMedia('(prefers-reduced-motion: reduce)').matches;
  const opt = (name: string, value: string, label: string, checked: boolean, title = '') => `<label${title ? ` title="${title}"` : ''}><input type="radio" name="${name}" value="${value}"${checked ? ' checked' : ''}><span>${label}</span></label>`;
  const check = (name: string, checked: boolean, label: string, disabled = false) => `<label class="check"><input type="checkbox" name="${name}"${checked ? ' checked' : ''}${disabled ? ' disabled' : ''}><span>${label}</span></label>`;
  const el = document.createElement('div');
  el.className = 'overlay';
  el.innerHTML = `
  <div class="pause" role="dialog" aria-modal="true" aria-labelledby="pause-title">
    <div class="pause-head">
      <svg class="hold" viewBox="0 0 76 40" aria-hidden="true">
        <path class="hold-track" d="${HOLD}"/>
        <path class="hold-fix" d="M56 5 L61 14 H51 Z"/>
        ${reducedMotion ? '<circle class="hold-ac" r="3" cx="38" cy="10"/>' : `<circle class="hold-ac" r="3"><animateMotion dur="9s" repeatCount="indefinite" path="${HOLD}"/></circle>`}
      </svg>
      <div>
        <h2 id="pause-title">Paused</h2>
        <p class="pause-sub">Holding. Nothing moves until you resume.</p>
      </div>
    </div>
    <button class="btn-primary" type="button" data-act="resume">Resume</button>
    <form class="pause-settings">
      <fieldset class="panel"><legend>Realism</legend>
        <div class="seg seg-row">${(Object.keys(LEVEL_LABEL) as RealismLevel[]).map((l) => opt('realism', l, LEVEL_LABEL[l], l === s.realism)).join('')}</div>
        <label class="field" title="Applies to the next session"><span>Emergencies (next session)</span><select name="emergencies">${EMERGENCIES.map(([v, label]) => `<option value="${v}"${s.emergencies === v ? ' selected' : ''}>${label}</option>`).join('')}</select></label>
      </fieldset>
      <fieldset class="panel"><legend>Conditions</legend>
        <label class="field" title="The clock jumps to this time and runs on with sim time"><span>Time of day</span><select name="timeOfDay">${TIMES_OF_DAY.map((t) => `<option value="${t}"${s.timeOfDay === t ? ' selected' : ''}>${TIME_OF_DAY_LABEL[t]}</option>`).join('')}</select></label>
        <label class="field" title="Sky, visibility and wind strength"><span>Weather</span><select name="weather">${WEATHERS.map((w) => `<option value="${w}"${s.weather === w ? ' selected' : ''}>${WEATHER[w].label}</option>`).join('')}</select></label>
      </fieldset>
      <fieldset class="panel"><legend>Display</legend>
        <div class="seg seg-row">${VIEWS.map((v) => opt('layout', v.layout, v.label, v.layout === s.layout, v.hint)).join('')}</div>
        <label class="field"><span>Graphics</span><select name="graphicsQuality">${GRAPHICS.map(([q, label]) => `<option value="${q}"${String(s.graphicsQuality) === q ? ' selected' : ''}>${label}</option>`).join('')}</select></label>
        ${check('side', s.side, 'Show strips and radio')}
        ${check('assists', s.assists, 'Assist (suggested instruction and what to say)')}
        <label class="field"><span>STARs and approaches</span><select name="procedures">${PROCEDURES.map(([v, label]) => `<option value="${v}"${s.procedures === v ? ' selected' : ''}>${label}</option>`).join('')}</select></label>
        ${check('trail', s.trail, 'Flight trail and route ahead for the selected aircraft')}
      </fieldset>
      ${o.ai ? `<fieldset class="panel pause-ai"><legend>AI this session</legend>
        <dl>${o.ai.rows.map((r) => `<dt>${esc(r.name)}</dt><dd>${esc(r.value)}${r.why ? `<small>${esc(r.why)}</small>` : ''}</dd>`).join('')}</dl>
      </fieldset>` : ''}
      <fieldset class="panel"><legend>Radio and sound</legend>
        ${check('voice', s.voice, s.radioProvider === 'openai' ? 'Read radio calls aloud (AI-generated voices)' : 'Read radio calls aloud', o.allowVoicePlayback === false)}
        ${check('speechInput', s.speechInput, 'Speak on the radio (hold Space) — uses AI', o.allowSpeechInput === false)}
        ${check('echoSpeech', s.echoSpeech, 'Replay my spoken calls in the controller voice', o.allowSpeechInput === false || o.allowVoicePlayback === false)}
        ${check('sound', s.sound, 'Alert tones')}
        ${check('radioAccents', s.radioAccents, 'Accented AI voices (each crew sounds like its airline)')}
        <label class="field" title="Carrier hiss and squelch under the radio voices"><span>Radio noise</span><select name="radioNoise">${RADIO_NOISE.map(([v, label]) => `<option value="${v}"${s.radioNoise === v ? ' selected' : ''}>${label}</option>`).join('')}</select></label>
      </fieldset>
    </form>
    <div class="pause-actions">
      <button class="btn" type="button" data-act="brief">${esc(o.briefingLabel ?? 'Airport briefing')}</button>
      <button class="btn" type="button" data-act="fullscreen"></button>
      <button class="btn" type="button" data-act="restart">Restart session</button>
      <button class="btn" type="button" data-act="end">End session</button>
      <button class="btn" type="button" data-act="feedback">Send feedback</button>
      ${o.onSessionLog ? '<button class="btn" type="button" data-act="session-log" title="Everything said, done and flown so far, as one file for working out what happened">Download session log</button>' : ''}
      <button class="btn pause-quit" type="button" data-act="quit">Quit to main menu</button>
    </div>
  </div>`;
  root.appendChild(el);

  const fsBtn = el.querySelector<HTMLButtonElement>('[data-act=fullscreen]')!;
  const syncFs = () => { fsBtn.textContent = isFullscreen() ? 'Exit full screen' : 'Full screen'; };
  const offFs = onFullscreenChange(syncFs);
  syncFs();

  el.querySelector('form')!.addEventListener('change', (e) => {
    const t = e.target as HTMLInputElement;
    if (t.name === 'voice') o.onChange({ voice: t.checked });
    else if (t.name === 'speechInput') o.onChange({ speechInput: t.checked, voiceImmediate: false });
    else if (t.name === 'graphicsQuality') o.onChange({ graphicsQuality: t.value === 'auto' ? 'auto' : Number(t.value) as 0 | 1 | 2 | 3 });
    else if (t.name === 'assists') o.onChange({ assists: t.checked });
    else if (t.name === 'procedures') o.onChange({ procedures: t.value as SessionSettings['procedures'] });
    else if (t.name === 'trail') o.onChange({ trail: t.checked });
    else if (t.name === 'emergencies') o.onChange({ emergencies: t.value as SessionSettings['emergencies'] });
    else if (t.name === 'side') o.onChange({ side: t.checked });
    else if (t.name === 'echoSpeech') o.onChange({ echoSpeech: t.checked });
    else if (t.name === 'sound') o.onChange({ sound: t.checked });
    else if (t.name === 'radioAccents') o.onChange({ radioAccents: t.checked });
    else if (t.name === 'radioNoise') o.onChange({ radioNoise: t.value as SessionSettings['radioNoise'] });
    else if (t.name === 'timeOfDay') o.onChange({ timeOfDay: t.value as TimeOfDay });
    else if (t.name === 'weather') o.onChange({ weather: t.value as Weather });
    else if (t.name === 'realism') o.onChange({ realism: t.value as RealismLevel });
    else if (t.name === 'layout') o.onChange({ layout: t.value as Layout });
  });
  el.addEventListener('click', (e) => {
    const act = (e.target as HTMLElement).closest<HTMLElement>('[data-act]')?.dataset.act;
    if (act === 'resume' || e.target === el) o.onResume();
    else if (act === 'fullscreen') o.onFullscreen();
    else if (act === 'restart') o.onRestart();
    else if (act === 'end') o.onEnd();
    else if (act === 'brief') o.onBriefing();
    else if (act === 'feedback') o.onFeedback();
    else if (act === 'session-log') o.onSessionLog?.();
    else if (act === 'quit') o.onQuit();
  });
  const disposeNavigation = mountMenuNavigation(el, o.onResume);
  el.querySelector<HTMLButtonElement>('[data-act=resume]')!.focus();
  return () => { disposeNavigation(); offFs(); el.remove(); };
}
